import { type CSSProperties, type FC, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { EyeIcon } from 'assets/svg/sprite'

import './_groupCirclesStory.scss'

type GroupAvatar = {
  id: string
  url: string | null
  displayName: string
}

type AvatarStyle = CSSProperties & {
  '--avatar-gradient'?: string
}

const avatarGradients = [
  'linear-gradient(140.46deg, #93ceff 14.38%, #2894ff 94.98%)', // blue
  'linear-gradient(140.46deg, #b9a7ff 14.38%, #7257ee 94.98%)', // purple
  'linear-gradient(140.46deg, #86e8f2 14.38%, #258fdc 94.98%)', // cyan
  'linear-gradient(140.46deg, #9ec8ff 14.38%, #4e63ea 94.98%)', // indigo
  'linear-gradient(140.46deg, #b3e4ff 14.38%, #3578e5 94.98%)', // sky
  'linear-gradient(140.46deg, #c1a8ff 14.38%, #8854e8 94.98%)', // violet
  'linear-gradient(140.46deg, #82d8ff 14.38%, #2766dd 94.98%)', // royal blue
  'linear-gradient(140.46deg, #9be9df 14.38%, #258fbc 94.98%)', // aqua
  'linear-gradient(140.46deg, #aab7ff 14.38%, #5952df 94.98%)', // blue violet
  'linear-gradient(140.46deg, #8fdcff 14.38%, #5a53e8 94.98%)'  // cyan purple
]

const hashString = (value: string) => {
  let hash = 0

  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0
  }

  return Math.abs(hash)
}

const GroupCirclesStory: FC<{
  group: {
    avatars: GroupAvatar[]
    count: number
  }
  customClass?: string
  onClick?: () => void
}> = ({
  group,
  customClass,
  onClick
}) => {
  const { t } = useTranslation()

  const visibleAvatars = useMemo(() => {
    const used = new Set<number>()

    return group.avatars.slice(0, 3).map((avatar, index) => {
      if (avatar.url) {
        return {
          ...avatar,
          gradient: null
        }
      }

      const seed = avatar.id || avatar.displayName || String(index)
      let gradientIndex = hashString(seed) % avatarGradients.length

      for (let i = 0; i < avatarGradients.length; i++) {
        const candidate = (gradientIndex + i) % avatarGradients.length

        if (!used.has(candidate)) {
          gradientIndex = candidate
          used.add(candidate)
          break
        }
      }

      return {
        ...avatar,
        gradient: avatarGradients[gradientIndex]
      }
    })
  }, [group.avatars])

  return (
    <div
      onClick={onClick}
      className={`groupCirclesStory ${customClass ?? ''}`}
    >
      <div className='groupCirclesStory__avatars'>
        {visibleAvatars.map((avatar, idx) => {
          const initial =
            avatar.displayName.trim().charAt(0).toUpperCase() || '?'

          const style: AvatarStyle | undefined = avatar.gradient
            ? {
                '--avatar-gradient': avatar.gradient
              }
            : undefined

          return (
            <div
              key={avatar.id || idx}
              className={`groupCirclesStory__avatar ${
                avatar.url ? '' : 'is-placeholder'
              }`}
              style={style}
            >
              {avatar.url ? (
                <img
                  src={avatar.url}
                  alt={t('avatar')}
                  loading='lazy'
                  decoding='async'
                  referrerPolicy='no-referrer'
                />
              ) : (
                <span className='groupCirclesStory__initial'>
                  {initial}
                </span>
              )}
            </div>
          )
        })}
      </div>

      <div className='groupCirclesStory__count'>
        <EyeIcon />
        <span>{group.count}</span>
      </div>
    </div>
  )
}

export default GroupCirclesStory